
import { useState, useCallback, useRef } from "react";
import { useProductDescriptionQuery } from "./product-descriptions/useProductDescriptionQuery";
import { useQueryClient } from "@tanstack/react-query";
import { ProductDescription } from "@/lib/api";
import { productService } from "@/services/productService";
import { productEditService } from "@/services/productEditService";
import { toast } from "sonner";
import { operationQueue } from "@/utils/operationQueue";
import { cacheUtils } from "@/utils/cacheUtils";

/**
 * Centralized hook for working with product descriptions
 */
export const useProductDescriptions = (collectionId?: string) => {
  // Use existing query hook for data fetching
  const { descriptions, isLoading, refetch } = useProductDescriptionQuery(collectionId);
  
  // State for editing
  const [editingProduct, setEditingProduct] = useState<ProductDescription | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [improvingDescriptionId, setImprovingDescriptionId] = useState<string | null>(null);
  const [savingDescriptionId, setSavingDescriptionId] = useState<string | null>(null);
  
  // State for force remounting
  const [remountCounter, setRemountCounter] = useState(0);
  const forceRemount = () => setRemountCounter(prev => prev + 1);
  
  // Query client for cache invalidation
  const queryClient = useQueryClient();
  
  // Edit handlers
  const handleEdit = useCallback((description: ProductDescription) => {
    setEditingProduct(description);
    setIsEditDialogOpen(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingProduct(null);
    setIsEditDialogOpen(false);
  }, []);

  // Save edit with proper error handling
  const handleSaveEdit = useCallback(async (
    product: ProductDescription,
    editedText: string,
    originalText: string
  ) => {
    if (!product?.id) return;
    
    setSavingDescriptionId(product.id);
    
    try {
      // Show optimistic toast
      const toastId = toast.loading(
        product.content_id 
          ? "Updating all variants in this group..." 
          : "Saving description..."
      );
      
      // Use our dedicated service for the update
      const result = await productEditService.editProduct(
        product,
        editedText,
        originalText
      );
      
      if (result.success) {
        // Success toast
        toast.success(
          product.content_id 
            ? `Updated ${result.updatedCount} product variants` 
            : "Description updated successfully",
          { id: toastId }
        );
        
        // Close dialog
        setEditingProduct(null);
        setIsEditDialogOpen(false);
        
        // Use consistent query keys for cache invalidation
        queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
        queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
        
        // Force a refresh
        refetch();
        forceRemount();
      } else {
        // Error toast
        toast.error(`Failed to update: ${result.error || "Unknown error"}`, { id: toastId });
      }
    } catch (error: any) {
      console.error("Error saving edit:", error);
      toast.error(`Error: ${error.message || "Failed to update description"}`);
    } finally {
      setSavingDescriptionId(null);
    }
  }, [queryClient, collectionId, refetch]);

  // Action handlers
  const handleApprove = useCallback(async (description: ProductDescription) => {
    try {
      const success = await productService.approveDescription(description);
      
      if (success) {
        toast.success(description.content_id 
          ? "All variants have been approved"
          : "Description approved successfully"
        );
        queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
        queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
      }
    } catch (error) {
      console.error("Error approving description:", error);
      toast.error("Failed to approve description");
    }
  }, [queryClient, collectionId]);

  const handleRevoke = useCallback(async (description: ProductDescription) => {
    try {
      const success = await productService.revokeApproval(description);
      
      if (success) {
        toast.success(description.content_id
          ? "Approval revoked for all variants"
          : "Approval revoked"
        );
        queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
        queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
      }
    } catch (error) {
      console.error("Error revoking approval:", error);
      toast.error("Failed to revoke approval");
    }
  }, [queryClient, collectionId]);

  const handleImprove = useCallback(async (description: ProductDescription) => {
    setImprovingDescriptionId(description.id);
    
    try {
      const success = await productService.improveDescription(description);
      
      if (success) {
        toast.success("Description improved successfully");
        queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
        queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
      }
    } catch (error) {
      console.error("Error improving description:", error);
      toast.error("Failed to improve description");
    } finally {
      setImprovingDescriptionId(null);
    }
  }, [queryClient, collectionId]);

  const handleDelete = useCallback(async (description: ProductDescription) => {
    if (!window.confirm("Are you sure you want to delete this description?")) {
      return;
    }
    
    try {
      const success = await productService.deleteDescription(description);
      
      if (success) {
        toast.success("Description deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
        queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
      }
    } catch (error) {
      console.error("Error deleting description:", error);
      toast.error("Failed to delete description");
    }
  }, [queryClient, collectionId]);

  // ✅ Improved refreshData function - consistent query keys
  const refreshData = useCallback(async () => {
    console.log("Refreshing data with improved cache handling");
    
    // Use consistent query keys for invalidation
    queryClient.invalidateQueries({ queryKey: ["productDescriptions", collectionId] });
    queryClient.invalidateQueries({ queryKey: ["collectionStats"] });
    
    // Trigger refetch
    await refetch();
    
    forceRemount();
  }, [queryClient, collectionId, refetch]);

  // Return all the functions and data needed
  return {
    descriptions,
    isLoading,
    editingProduct,
    isEditDialogOpen,
    handleEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleApprove, 
    handleRevoke, 
    handleImprove, 
    handleDelete, 
    refreshData,
    improvingDescriptionId,
    savingDescriptionId, 
    isSaving: savingDescriptionId !== null,
    isOperationInProgress: operationQueue.activeCount > 0,
    remountCounter,
  };
};
